// ============================================
// Seed Templates: Employment & Contractor
// Category: employment-contractor
// Templates: 8
// ============================================

import type { DocTemplate } from '../../types/docops';

export const SEED_TEMPLATES_EMPLOYMENT: DocTemplate[] = [
  // ── 7.1 CEO Employment Agreement ──
  {
    id: '7.1-ceo-employment-agreement',
    categoryId: 'employment-contractor',
    title: 'CEO Employment Agreement',
    description:
      'Employment agreement outline for the CEO covering authority, compensation, equity, change of control, non-compete, IP assignment, and termination provisions.',
    templateContent: `# CEO Employment Agreement

**{{brandEntity}}**

CEO: {{ceoName}}
Email: {{ceoEmail}}
Governing Law: {{stateOfFormation}}

## Authority and Responsibilities
{{AI_GENERATED_CONTENT}}

## Compensation Structure
{{AI_GENERATED_CONTENT}}

## Change of Control Provisions
{{AI_GENERATED_CONTENT}}

## Non-Compete / Non-Solicitation
{{AI_GENERATED_CONTENT}}

## Confidentiality
{{AI_GENERATED_CONTENT}}

## IP Assignment
{{AI_GENERATED_CONTENT}}

## Termination Provisions
{{AI_GENERATED_CONTENT}}

## Dispute Resolution
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'ceoEmail',
      'stateOfFormation',
    ],
    generationPrompt:
      'Generate CEO Employment Agreement outline for {{brandEntity}}. CEO: {{ceoName}}. Cover: authority and responsibilities (full operational control, sign contracts, manage finances, hire/fire), compensation structure (base salary, performance bonus, equity), benefits, change of control provisions (acceleration of equity, severance), non-compete (24 months, competing health-tech platforms), non-solicitation (24 months), confidentiality (perpetual), IP assignment (all work product is company property), termination provisions (with cause, without cause, resignation, disability, death), dispute resolution (arbitration in {{stateOfFormation}}). Format: outline for attorney review.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 15,
    sortOrder: 1,
    generationRound: 5,
    dependencies: ['2.1-operating-agreement', '3.4-ip-license-agreement'],
    anchorSections: ['§7', '§9', '§13'],
    inheritedProvisions: ['ip-licensed-model'],
  },

  // ── 7.2 Offer Letter Template ──
  {
    id: '7.2-offer-letter-template',
    categoryId: 'employment-contractor',
    title: 'Offer Letter Template',
    description:
      'Reusable offer letter template with fillable fields for position, compensation, equity, and standard employment terms.',
    templateContent: `# Offer Letter

**{{brandEntity}}** (d/b/a {{brandName}})

Dear Candidate,

We are pleased to offer you a position at {{brandEntity}} (d/b/a {{brandName}}).

The specific terms of your offer — including position title, start date, compensation, equity grant, and reporting structure — are set forth below.

## Terms
{{AI_GENERATED_CONTENT}}

Sincerely,

{{ceoName}}
Chief Executive Officer
{{brandEntity}}
`,
    requiredVariables: [
      'brandEntity',
      'brandName',
      'ceoName',
    ],
    generationPrompt: `Generate the Terms section of an Offer Letter for {{brandEntity}} (d/b/a {{brandName}}).

This is a reusable template. Where specific values vary per employee, describe the term category without using bracket placeholders.

SECTIONS TO GENERATE:
1. Position and Reporting — describe the standard reporting structure under {{ceoName}} (CEO)
2. Compensation — describe that base salary, performance bonus structure, and equity participation will be specified per the compensation schedule attached as Exhibit A
3. Benefits — standard benefits package including health insurance eligibility after 90 days, PTO policy, and any applicable retirement benefits
4. At-Will Employment — employment is at-will and may be terminated by either party at any time with or without cause
5. Background Check — offer is contingent upon satisfactory completion of a background check
6. HIPAA Training — all employees must complete HIPAA privacy and security training within 30 days of start date
7. Confidentiality and IP Assignment — employee must sign the {{brandEntity}} Confidentiality and IP Assignment Agreement as a condition of employment
8. Start Date — to be mutually agreed upon in writing
9. Acceptance — offer expires 14 days from date of letter; acceptance by signing and returning this letter

FORMAT RULES:
- Do NOT use bracket placeholders like [X], [Insert], [TBD], or [Employee Name]
- Use actual entity names from brand config ({{brandEntity}}, {{brandName}}, {{ceoName}})
- For per-employee values, use descriptive language ("as specified in the compensation schedule")
- Do NOT include meta-commentary or notes
- Professional legal letter tone`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 3,
    sortOrder: 2,
    generationRound: 5,
    dependencies: ['2.1-operating-agreement', '3.4-ip-license-agreement'],
    anchorSections: ['§7', '§9', '§13'],
    inheritedProvisions: ['ip-licensed-model'],
  },

  // ── 7.3 Independent Contractor Agreement ──
  {
    id: '7.3-contractor-agreement',
    categoryId: 'employment-contractor',
    title: 'Independent Contractor Agreement',
    description:
      'Reusable contractor agreement template covering scope of services, IP assignment, confidentiality, independent contractor status, and termination provisions.',
    templateContent: `# Independent Contractor Agreement

**{{brandEntity}}**

Governing Law: {{stateOfFormation}}

## Scope of Services
{{AI_GENERATED_CONTENT}}

## Compensation and Payment Terms
{{AI_GENERATED_CONTENT}}

## IP Assignment
{{AI_GENERATED_CONTENT}}

## Confidentiality
{{AI_GENERATED_CONTENT}}

## Independent Contractor Status
{{AI_GENERATED_CONTENT}}

## Insurance Requirements
{{AI_GENERATED_CONTENT}}

## Termination
{{AI_GENERATED_CONTENT}}

## Exhibit A — Statement of Work
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'legalEmail',
      'stateOfFormation',
    ],
    generationPrompt:
      'Generate Independent Contractor Agreement template for {{brandEntity}}. Covers: scope of services (defined per SOW), compensation (hourly/project), payment terms (net 30), IP assignment (all work product assigned to company), confidentiality, non-solicitation, independent contractor status (not employee — no benefits, taxes, workers comp), insurance requirements, termination (30 days notice), governing law ({{stateOfFormation}}). Format: reusable template with SOW exhibit.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 3,
    generationRound: 5,
    dependencies: ['2.1-operating-agreement'],
    anchorSections: ['§7'],
    inheritedProvisions: ['ip-licensed-model'],
  },

  // ── 7.4 Confidentiality & IP Assignment Agreement ──
  {
    id: '7.4-confidentiality-ip-assignment',
    categoryId: 'employment-contractor',
    title: 'Confidentiality & IP Assignment Agreement',
    description:
      'Agreement signed by all team members covering non-disclosure obligations, IP assignment of all work product, and return of materials on termination.',
    templateContent: `# Confidentiality & IP Assignment Agreement

**{{brandEntity}}** (d/b/a {{brandName}})

## Definition of Confidential Information
{{AI_GENERATED_CONTENT}}

## Non-Disclosure Obligations
{{AI_GENERATED_CONTENT}}

## IP Assignment
{{AI_GENERATED_CONTENT}}

## Prior Inventions Exclusion
{{AI_GENERATED_CONTENT}}

## Return of Materials
{{AI_GENERATED_CONTENT}}

## Exhibit A — Prior Inventions
[List any prior inventions here]

**Signature:** ___________________________

**Printed Name:** ___________________________

**Date:** ___________________________
`,
    requiredVariables: [
      'brandEntity',
      'brandName',
    ],
    generationPrompt:
      'Generate Confidentiality & IP Assignment Agreement for {{brandEntity}} (d/b/a {{brandName}}). All team members sign. Covers: definition of confidential information (trade secrets, PHI, business plans, code, customer data, financials), non-disclosure obligations (perpetual for trade secrets, 3 years for other), IP assignment (all inventions, works, discoveries during engagement are work-for-hire owned by company), prior inventions exclusion (Exhibit A), return of materials on termination. Format: complete agreement.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 5,
    sortOrder: 4,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 7.5 HIPAA Confidentiality Agreement ──
  {
    id: '7.5-hipaa-confidentiality',
    categoryId: 'employment-contractor',
    title: 'HIPAA Confidentiality Agreement',
    description:
      'PHI-specific confidentiality agreement signed by all workforce members covering minimum necessary access, breach reporting, and device security.',
    templateContent: `# HIPAA Confidentiality Agreement

**{{brandEntity}}**

## PHI-Specific Obligations
{{AI_GENERATED_CONTENT}}

## Breach Reporting
{{AI_GENERATED_CONTENT}}

## Device Security
{{AI_GENERATED_CONTENT}}

## Acknowledgment of Sanctions
{{AI_GENERATED_CONTENT}}

**Signature:** ___________________________

**Printed Name:** ___________________________

**Date:** ___________________________

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate HIPAA-specific Confidentiality Agreement for {{brandEntity}}. All workforce members sign. PHI-specific obligations: access only minimum necessary PHI for job duties, no unauthorized disclosure, report any suspected breach immediately to {{complianceEmail}}, secure workstations and devices, no PHI on personal devices unless approved, annual training completion. Acknowledgment of sanctions for violations. Format: short agreement with signature block.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 5,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 7.6 Non-Compete / Non-Solicitation Agreement ──
  {
    id: '7.6-non-compete-non-solicit',
    categoryId: 'employment-contractor',
    title: 'Non-Compete / Non-Solicitation Agreement',
    description:
      'Standalone non-compete and non-solicitation agreement for key personnel covering competing platforms, employee/customer solicitation, and enforceability provisions.',
    templateContent: `# Non-Compete / Non-Solicitation Agreement

**{{brandEntity}}**

Governing Law: {{stateOfFormation}}

## Non-Compete
{{AI_GENERATED_CONTENT}}

## Non-Solicitation
{{AI_GENERATED_CONTENT}}

## Consideration
{{AI_GENERATED_CONTENT}}

## Reasonableness Provisions
{{AI_GENERATED_CONTENT}}

**Signature:** ___________________________

**Printed Name:** ___________________________

**Date:** ___________________________
`,
    requiredVariables: [
      'brandEntity',
      'stateOfFormation',
    ],
    generationPrompt:
      'Generate Non-Compete and Non-Solicitation Agreement for {{brandEntity}} key personnel. Non-compete: 24 months, competing AI-powered health analysis or supplement recommendation platforms, geographic scope per {{stateOfFormation}} enforceability. Non-solicitation: 24 months, employees/contractors, customers/users, vendors/partners. Consideration: continued employment or [specified consideration for existing employees]. Reasonableness provisions per {{stateOfFormation}} law. Format: standalone agreement.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 4,
    sortOrder: 6,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 7.7 Equity Grant Agreement ──
  {
    id: '7.7-equity-grant-agreement',
    categoryId: 'employment-contractor',
    title: 'Equity Grant Agreement',
    description:
      'Template for membership interest grants covering vesting schedule, acceleration on change of control, repurchase rights, transfer restrictions, and tax provisions.',
    templateContent: `# Equity Grant Agreement

**{{brandEntity}}**

Governing Law: {{stateOfFormation}}

## Grant of Membership Interest
{{AI_GENERATED_CONTENT}}

## Vesting Schedule
{{AI_GENERATED_CONTENT}}

## Acceleration on Change of Control
{{AI_GENERATED_CONTENT}}

## Repurchase Rights
{{AI_GENERATED_CONTENT}}

## Transfer Restrictions
{{AI_GENERATED_CONTENT}}

## Drag-Along / Tag-Along Rights
{{AI_GENERATED_CONTENT}}

## Tax Provisions
{{AI_GENERATED_CONTENT}}

Signed by {{ceoName}} (CEO) on behalf of {{brandEntity}}.

**Company Signature:** ___________________________

**Grantee Signature:** ___________________________

**Date:** ___________________________
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'stateOfFormation',
    ],
    generationPrompt:
      'Generate Equity Grant Agreement template for {{brandEntity}}. Covers: membership interest grant, vesting schedule (4-year vest, 1-year cliff), acceleration on change of control (single trigger: 50% acceleration, double trigger: 100% acceleration), repurchase rights on termination (at fair market value), transfer restrictions, drag-along/tag-along rights, tax provisions (83(b) election notice). Signed by {{ceoName}} (CEO) on behalf of company. Format: template with fillable grant details.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 7,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 7.8 Employee Termination Checklist ──
  {
    id: '7.8-termination-checklist',
    categoryId: 'employment-contractor',
    title: 'Employee Termination Checklist',
    description:
      'Step-by-step checklist for employee/contractor offboarding covering system access revocation, device collection, HIPAA compliance, and post-termination obligations.',
    templateContent: `# Employee / Contractor Termination Checklist

**{{brandEntity}}**

**Employee Name:** ___________________________

**Termination Date:** ___________________________

| # | Step | Responsible Party | Completed | Date |
|---|------|-------------------|-----------|------|
| 1 | Revoke all system access (Firebase, GitHub, admin dashboard, email, Slack) | | | |
| 2 | Collect company devices and materials | | | |
| 3 | Verify IP assignment agreement on file | | | |
| 4 | Conduct exit interview | | | |
| 5 | Process final payment | | | |
| 6 | Disable VPN/remote access | | | |
| 7 | Update HIPAA access logs | | | |
| 8 | Confirm no PHI on personal devices | | | |
| 9 | Send termination confirmation letter | | | |
| 10 | Update org chart and access matrix | | | |
| 11 | Schedule 90-day post-termination non-compete reminder | | | |

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate Employee/Contractor Termination Checklist for {{brandEntity}}. Steps: 1) Revoke all system access (Firebase console, GitHub, admin dashboard, email, Slack), 2) Collect company devices and materials, 3) Verify IP assignment agreement on file, 4) Conduct exit interview, 5) Process final payment, 6) Disable VPN/remote access, 7) Update HIPAA access logs, 8) Confirm no PHI on personal devices, 9) Send termination confirmation letter, 10) Update org chart and access matrix, 11) Schedule 90-day post-termination non-compete reminder. Contact: {{complianceEmail}}. Format: checklist with responsible party and date columns.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 8,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
